import { React, useState, useEffect } from 'react'
import { Stack, Alert, Spinner, Button, Table, Modal, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Login from '../Components/Admin/Login';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import Navigation from '../Components/Admin/Navigation';

export default function UkiesAdminPages(props) {
    const auth = getAuth();
    const navigate = useNavigate();
    
    // State management
    const [token, setToken] = useState(localStorage.getItem('ukies-webinars-accessToken'))
    const [user, setUser] = useState(JSON.parse(localStorage.getItem('ukies-webinars-admin')))
    const [pages, setPages] = useState([])
    const [pagesLoading, setPagesLoading] = useState(false)
    const [selectedPage, setSelectedPage] = useState(null)
    const [showModal, setShowModal] = useState(false)
    const [formData, setFormData] = useState({})
    const [saving, setSaving] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [alertMessage, setAlertMessage] = useState('')
    const [alertType, setAlertType] = useState('success')
    const [refreshing, setRefreshing] = useState(false)
    const [authReady, setAuthReady] = useState(false)

    // Token for API calls
    const apiToken = process.env.REACT_APP_UKIES_API_TOKEN;

    // Auth state listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setAuthReady(true);
                fetchPages();
            } else {
                localStorage.removeItem('ukies-webinars-admin')
                localStorage.removeItem('ukies-webinars-accessToken')
                setToken()
                setUser()
                setAuthReady(false);
            }
        })
        
        return () => unsubscribe();
    }, []);

    // Set page title
    useEffect(() => {
        document.title = "Ukie's Admin - Pages";
    }, []);

    // Add custom styles
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            /* Override Bootstrap primary color */
            .btn-primary {
                background-color: #3b4f46 !important;
                border-color: #3b4f46 !important;
            }
            .btn-primary:hover {
                background-color: #2d3d35 !important;
                border-color: #2d3d35 !important;
            }
            .btn-primary:focus, .btn-primary.focus {
                background-color: #2d3d35 !important;
                border-color: #2d3d35 !important;
                box-shadow: 0 0 0 0.2rem rgba(59, 79, 70, 0.5) !important;
            }
            .btn-primary:not(:disabled):not(.disabled):active, .btn-primary:not(:disabled):not(.disabled).active {
                background-color: #243028 !important;
                border-color: #243028 !important;
            }
            .btn-outline-primary {
                color: #3b4f46 !important;
                border-color: #3b4f46 !important;
            }
            .btn-outline-primary:hover {
                background-color: #3b4f46 !important;
                border-color: #3b4f46 !important;
            }
            .btn-outline-primary:focus, .btn-outline-primary.focus {
                box-shadow: 0 0 0 0.2rem rgba(59, 79, 70, 0.5) !important;
            }
            .btn-outline-primary:not(:disabled):not(.disabled):active, .btn-outline-primary:not(:disabled):not(.disabled).active {
                background-color: #3b4f46 !important;
                border-color: #3b4f46 !important;
            }
            
            /* Add button styling with ukies green colors */
            .add-button-green {
                background-color: var(--ukies-light-green) !important;
                border-color: var(--ukies-light-green) !important;
                color: white !important;
            }
            .add-button-green:hover:not(:disabled) {
                background-color: var(--ukies-dark-green) !important;
                border-color: var(--ukies-dark-green) !important;
                color: white !important;
            }
            .add-button-green:focus:not(:disabled) {
                background-color: var(--ukies-dark-green) !important;
                border-color: var(--ukies-dark-green) !important;
                color: white !important;
                box-shadow: 0 0 0 0.2rem rgba(59, 79, 70, 0.25) !important;
            }
            .add-button-green:active:not(:disabled) {
                background-color: var(--ukies-dark-green) !important;
                border-color: var(--ukies-dark-green) !important;
                color: white !important;
            }

            /* Refresh button styling */
            .refresh-button:hover:not(:disabled) {
                background-color: #567a70 !important;
                border-color: #567a70 !important;
            }

            /* Modal form styling */
            .modal .form-control {
                font-size: 0.9rem !important;
            }
            .modal .form-select {
                font-size: 0.9rem !important;
            }
            .modal .form-label {
                font-size: 0.7rem !important;
            }
            .modal textarea.form-control {
                font-size: 0.9rem !important;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Helper function to build hierarchical page structure
    const buildPageHierarchy = (pages) => {
        // Create a map for quick lookup
        const pageMap = new Map();
        pages.forEach(page => {
            pageMap.set(page.id, { ...page, children: [] });
        });

        // Build hierarchy and calculate full slugs
        const rootPages = [];
        pages.forEach(page => {
            const pageWithChildren = pageMap.get(page.id);
            
            if (page.parent_id && pageMap.has(page.parent_id)) {
                // This is a child page
                const parent = pageMap.get(page.parent_id);
                parent.children.push(pageWithChildren);
                
                // Build full slug including parent path
                const buildFullSlug = (currentPage) => {
                    const parts = [];
                    let current = currentPage;
                    while (current) {
                        if (current.slug) {
                            parts.unshift(current.slug);
                        }
                        current = current.parent_id ? pageMap.get(current.parent_id) : null;
                    }
                    return parts.join('/');
                };
                pageWithChildren.fullSlug = buildFullSlug(page);
            } else {
                // This is a root page
                rootPages.push(pageWithChildren);
                pageWithChildren.fullSlug = page.slug || '';
            }
        });

        // Sort function that respects position
        const sortByPosition = (a, b) => {
            const posA = a.position || 0;
            const posB = b.position || 0;
            return posA - posB;
        };

        // Sort root pages and their children recursively
        const sortRecursively = (pageArray) => {
            pageArray.sort(sortByPosition);
            pageArray.forEach(page => {
                if (page.children.length > 0) {
                    sortRecursively(page.children);
                }
            });
        };

        sortRecursively(rootPages);
        return rootPages;
    };

    // Helper function to flatten hierarchy for display
    const flattenHierarchy = (pages, level = 0) => {
        const result = [];
        pages.forEach(page => {
            result.push({ ...page, level });
            if (page.children && page.children.length > 0) {
                result.push(...flattenHierarchy(page.children, level + 1));
            }
        });
        return result;
    };

    // Fetch pages from MeiliSearch
    const fetchPages = async () => {
        if (!apiToken) return;
        
        setPagesLoading(true);
        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_pages/search', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${apiToken}`
                },
                body: JSON.stringify({
                    "q": null,
                    "sort": ["id:desc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "filter":[["webshop_id='1'"]],
                    "limit": 1000
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            // Build hierarchical structure and then flatten for display
            const hierarchicalPages = buildPageHierarchy(data.hits || []);
            const flattenedPages = flattenHierarchy(hierarchicalPages);
            
            setPages(flattenedPages);
        } catch (error) {
            console.error('Error fetching pages:', error);
            setShowAlert(true);
            setAlertMessage('Error loading pages. Please try again.');
            setAlertType('danger');
        } finally {
            setPagesLoading(false);
        }
    };

    // Handle row click
    const handleRowClick = (page) => {
        navigate(`/ukies-admin/pages/${page?.id}`);
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle save
    const handleSave = async () => {
        alert('Not implemented yet');
        // if (!selectedPage) return;
        
        // setSaving(true);
        // try {
        //     // Update page via MeiliSearch API
        //     const response = await fetch(`https://search.eliteswitch.pro/indexes/ukies-pages/documents`, {
        //         method: 'PUT',
        //         headers: {
        //             'Content-Type': 'application/json',
        //             'Authorization': `Bearer ${apiToken}`
        //         },
        //         body: JSON.stringify([{
        //             ...selectedPage,
        //             ...formData,
        //             name: formData.title, // Ensure both name and title are updated
        //             title: formData.title,
        //             updated_at: new Date().toISOString()
        //         }])
        //     });

        //     if (!response.ok) {
        //         throw new Error(`HTTP error! status: ${response.status}`);
        //     }

        //     setShowAlert(true);
        //     setAlertMessage('Page updated successfully!');
        //     setAlertType('success');
        //     setShowModal(false);
            
        //     // Refresh pages list
        //     setTimeout(() => {
        //         fetchPages();
        //     }, 1000);
            
        // } catch (error) {
        //     console.error('Error saving page:', error);
        //     setShowAlert(true);
        //     setAlertMessage('Error saving page. Please try again.');
        //     setAlertType('danger');
        // } finally {
        //     setSaving(false);
        // }
    };

    // Handle close modal
    const handleClose = () => {
        setShowModal(false);
        setSelectedPage(null);
        setFormData({});
    };

    // Handle refresh
    const handleRefreshData = async () => {
        setRefreshing(true);
        try {
            await fetchPages();
            setShowAlert(true);
            setAlertMessage('Pages refreshed successfully!');
            setAlertType('success');
        } catch (error) {
            setShowAlert(true);
            setAlertMessage('Error refreshing pages.');
            setAlertType('danger');
        } finally {
            setRefreshing(false);
        }
    };

    // Handle add new page
    const handleAddNewPage = async () => {
        alert('Deze functie is nog niet beschikbaar');

    };

    // If not authenticated, show login
    if (!token) {
        return <Login user={user} setUser={setUser} token={token} setToken={setToken} />
    }

    return (
        <>
            <Navigation user={user} currentPage='pages' />
            {showAlert && (
                <Alert 
                    variant={alertType} 
                    dismissible 
                    onClose={() => setShowAlert(false)} 
                    style={{
                        position: 'fixed',
                        top: '15px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1060,
                        minWidth: '300px',
                        maxWidth: '600px'
                    }}
                >
                    {alertMessage}
                </Alert>
            )}
            
            <Stack style={{marginTop:'50px',padding:'1rem 0'}}>
                <h1 style={{fontSize:'2.5rem'}}>Pages</h1>
                
                {pagesLoading ? (
                    <div className="text-center p-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <div>Loading pages...</div>
                    </div>
                ) : (
                    <Table striped hover style={{cursor:'pointer'}}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'center' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 4.5C7.5 4.5 3.75 7.5 1.5 12C3.75 16.5 7.5 19.5 12 19.5C16.5 19.5 20.25 16.5 22.5 12C20.25 7.5 16.5 4.5 12 4.5ZM12 17C9.75 17 7.5 15.75 7.5 12C7.5 8.25 9.75 7 12 7C14.25 7 16.5 8.25 16.5 12C16.5 15.75 14.25 17 12 17ZM12 9C10.5 9 9 10.5 9 12C9 13.5 10.5 15 12 15C13.5 15 15 13.5 15 12C15 10.5 13.5 9 12 9Z" fill="currentColor"/>
                                    </svg>
                                </th>
                                <th>Name</th>
                                <th>URL</th>
                            </tr>
                        </thead>
                        <tbody>
                             {pages.map((page) => {
                                 const isVisible = page.status === null ? true : page.status === 'published';
                                 const indentLevel = page.level || 0;
                                 const indentStyle = {
                                     paddingLeft: `${indentLevel * 20 + 8}px`
                                 };
                                 
                                 return (
                                     <tr 
                                         key={page.id} 
                                         onClick={() => handleRowClick(page)}
                                     >
                                         <td style={{ textAlign: 'center', color: isVisible ? '#28a745' : '#dc3545' }}>
                                             {isVisible ? '✓' : '✗'}
                                         </td>
                                         <td style={indentStyle}>
                                             {page.name || page.title || 'Untitled'}
                                         </td>
                                         <td>{page.fullSlug ? `/${page.fullSlug}` : '-'}</td>
                                     </tr>
                                 );
                             })}
                         </tbody>
                    </Table>
                )}

                {!pagesLoading && pages.length === 0 && (
                    <div className="text-center p-4">
                        <p>No pages found.</p>
                        <Button variant="primary" onClick={handleAddNewPage}>
                            Create First Page
                        </Button>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex justify-content-end mt-3 gap-2 mb-3">
                    <Button 
                        variant="success" 
                        size="sm"
                        onClick={handleAddNewPage}
                        disabled={refreshing || pagesLoading}
                        title="Click to add a new page"
                        className="add-button-green"
                    >
                        + Add Page
                    </Button>
                    <Button 
                        variant="primary" 
                        size="sm"
                        onClick={handleRefreshData}
                        disabled={refreshing || pagesLoading}
                        title="Click to refresh data"
                        className="refresh-button"
                    >
                        {refreshing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Refreshing...
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-clockwise me-2" viewBox="0 0 16 16">
                                    <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                                    <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                                </svg>
                                Refresh
                            </>
                        )}
                    </Button>
                </div>
            </Stack>

            {/* Edit Page Modal */}
            <Modal show={showModal} onHide={handleClose} size="lg" backdrop={true} keyboard={true}>
                <Modal.Header closeButton>
                    <div>
                        <Modal.Title>{selectedPage?.title || selectedPage?.name || 'Edit Page'}</Modal.Title>
                        {selectedPage?.id && (
                            <small className="text-muted">{selectedPage.id}</small>
                        )}
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                name="title"
                                value={formData.title || ''}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Slug</Form.Label>
                            <Form.Control
                                type="text"
                                name="slug"
                                value={formData.slug || ''}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                name="status"
                                value={formData.status || 'draft'}
                                onChange={handleInputChange}
                            >
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="archived">Archived</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Position</Form.Label>
                            <Form.Control
                                type="number"
                                name="position"
                                value={formData.position || 0}
                                onChange={handleInputChange}
                                min="0"
                            />
                            <Form.Text className="text-muted">
                                Used for sorting pages. Lower numbers appear first.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Meta Title</Form.Label>
                            <Form.Control
                                type="text"
                                name="meta_title"
                                value={formData.meta_title || ''}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Meta Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="meta_description"
                                value={formData.meta_description || ''}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Meta Keywords</Form.Label>
                            <Form.Control
                                type="text"
                                name="meta_keywords"
                                value={formData.meta_keywords || ''}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} className="me-2">
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => navigate(`/ukies-admin/pages/${selectedPage?.id}`)}
                        disabled={!selectedPage}
                        className="me-2"
                    >
                        Designer
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
} 